import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

const DEFAULT_CHANGED_TTL_MS = 1500;
const DEFAULT_SELF_EDIT_TTL_MS = 2000;

interface RecentlyChangedRowsContextValue {
  // Bumped on every map change so consumers re-evaluate via useContext.
  version: number;
  changed: Map<string, number>;
  selfEdits: Map<string, number>;
  markChanged: (table: string, id: string) => void;
  markSelfEdit: (table: string, id: string, ttlMs?: number) => void;
  isSelfEdit: (table: string, id: string) => boolean;
}

function makeKey(table: string, id: string): string {
  return `${table}:${id}`;
}

function isSelfEditExpiry(map: Map<string, number>, key: string, now: number): boolean {
  const expiry = map.get(key);
  if (expiry === undefined) return false;
  if (expiry <= now) {
    map.delete(key);
    return false;
  }
  return true;
}

const noopValue: RecentlyChangedRowsContextValue = {
  version: 0,
  changed: new Map(),
  selfEdits: new Map(),
  markChanged: () => {},
  markSelfEdit: () => {},
  isSelfEdit: () => false,
};

const RecentlyChangedRowsContext = createContext<RecentlyChangedRowsContextValue>(noopValue);

export function RecentlyChangedRowsProvider({ children }: { children: React.ReactNode }) {
  const changedRef = useRef<Map<string, number>>(new Map());
  const selfEditsRef = useRef<Map<string, number>>(new Map());
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const markChanged = useCallback((table: string, id: string) => {
    if (!id) return;
    const key = makeKey(table, id);
    if (isSelfEditExpiry(selfEditsRef.current, key, Date.now())) return;
    changedRef.current.set(key, Date.now() + DEFAULT_CHANGED_TTL_MS);
    bump();
    setTimeout(() => {
      const expiry = changedRef.current.get(key);
      if (expiry !== undefined && expiry <= Date.now()) {
        changedRef.current.delete(key);
        bump();
      }
    }, DEFAULT_CHANGED_TTL_MS + 16);
  }, [bump]);

  const markSelfEdit = useCallback((table: string, id: string, ttlMs?: number) => {
    if (!id) return;
    const ttl = ttlMs ?? DEFAULT_SELF_EDIT_TTL_MS;
    const key = makeKey(table, id);
    selfEditsRef.current.set(key, Date.now() + ttl);
    setTimeout(() => {
      const expiry = selfEditsRef.current.get(key);
      if (expiry !== undefined && expiry <= Date.now()) {
        selfEditsRef.current.delete(key);
      }
    }, ttl + 16);
  }, []);

  const isSelfEdit = useCallback((table: string, id: string) => {
    if (!id) return false;
    return isSelfEditExpiry(selfEditsRef.current, makeKey(table, id), Date.now());
  }, []);

  const value = useMemo<RecentlyChangedRowsContextValue>(() => ({
    version,
    changed: changedRef.current,
    selfEdits: selfEditsRef.current,
    markChanged,
    markSelfEdit,
    isSelfEdit,
  }), [version, markChanged, markSelfEdit, isSelfEdit]);

  return (
    <RecentlyChangedRowsContext.Provider value={value}>
      {children}
    </RecentlyChangedRowsContext.Provider>
  );
}

/**
 * Returns true if (table, id) is in the recently-changed set.
 * Re-renders the calling component when the underlying set changes (via context version bump).
 */
export function useRecentlyChanged(table: string, id: string): boolean {
  const ctx = useContext(RecentlyChangedRowsContext);
  void ctx.version;
  if (!id) return false;
  const expiry = ctx.changed.get(makeKey(table, id));
  if (expiry === undefined) return false;
  return expiry > Date.now();
}

export interface RecentlyChangedRowsApi {
  markChanged: (table: string, id: string) => void;
  markSelfEdit: (table: string, id: string, ttlMs?: number) => void;
  isSelfEdit: (table: string, id: string) => boolean;
}

/**
 * Imperative API used by mutation hooks (markSelfEdit) and the realtime hook (markChanged, isSelfEdit).
 */
export function useRecentlyChangedRowsApi(): RecentlyChangedRowsApi {
  const ctx = useContext(RecentlyChangedRowsContext);
  return useMemo(() => ({
    markChanged: ctx.markChanged,
    markSelfEdit: ctx.markSelfEdit,
    isSelfEdit: ctx.isSelfEdit,
  }), [ctx.markChanged, ctx.markSelfEdit, ctx.isSelfEdit]);
}
